import React, { useState, useEffect, useCallback } from 'react';
import { collection, query, where, orderBy, getDocs } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db, app } from '../../../../firebase';
import {
  Plus, Pencil, Trash2, Copy, RefreshCw, Loader2,
  CheckSquare, Square, X, AlertTriangle, CheckCircle2,
  Calendar, ChevronDown, ChevronUp,
} from 'lucide-react';

// ─── constants ─────────────────────────────────────────────────────────────────

const DAY_NAMES = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];

// Categorías alineadas con restriccionVehicularService.js
// Cada categoría incluye rango de año para filtrado en admin y display en lista
const CATEGORIAS = [
  { value: 'sello_verde',               label: 'Autos sello verde',        rango: '1994–2011', grupo: 'sello_verde' },
  { value: 'camioneta_sello_verde',     label: 'Camionetas sello verde',   rango: '1994–2011', grupo: 'sello_verde' },
  { value: 'moto_sello_verde',          label: 'Motos sello verde',        rango: '2002–2009', grupo: 'sello_verde' },
  { value: 'sin_sello_verde',           label: 'Autos sin sello verde',    rango: '≤ 1993',    grupo: 'sin_sello_verde' },
  { value: 'camioneta_sin_sello_verde', label: 'Camionetas sin sello verde', rango: '≤ 1993',  grupo: 'sin_sello_verde' },
  { value: 'moto_sin_sello_verde',      label: 'Motos sin sello verde',    rango: '≤ 2001',    grupo: 'sin_sello_verde' },
  { value: 'camion_sin_sello_verde',    label: 'Camiones sin sello verde', rango: null,         grupo: 'sin_sello_verde' },
  { value: 'bus_sin_sello_verde',       label: 'Buses sin sello verde',    rango: null,         grupo: 'sin_sello_verde' },
  { value: 'general',                   label: 'General',                  rango: null,         grupo: null },
];

// ─── Generación de temporada completa ──────────────────────────────────────────

// Feriados dentro de la temporada de restricción (mayo–agosto)
const FERIADOS_TEMPORADA = {
  '2025-05-01': 'Día del Trabajo', '2025-05-21': 'Glorias Navales',
  '2025-06-21': 'Pueblos Indígenas', '2025-06-30': 'San Pedro y San Pablo',
  '2025-07-16': 'Virgen del Carmen', '2025-08-15': 'Asunción de la Virgen',
  '2026-05-01': 'Día del Trabajo', '2026-05-21': 'Glorias Navales',
  '2026-06-21': 'Pueblos Indígenas', '2026-06-29': 'San Pedro y San Pablo',
  '2026-07-16': 'Virgen del Carmen', '2026-08-15': 'Asunción de la Virgen',
  '2027-05-01': 'Día del Trabajo', '2027-05-21': 'Glorias Navales',
  '2027-06-21': 'Pueblos Indígenas', '2027-06-28': 'San Pedro y San Pablo',
  '2027-07-16': 'Virgen del Carmen', '2027-08-15': 'Asunción de la Virgen',
};

// Rotación de dígitos 2026 (idéntica a restriccionVehicularService.js)
const DIGITOS_ROTACION = {
  sello_verde:     { 1:['8','9'], 2:['0','1'], 3:['2','3'], 4:['4','5'], 5:['6','7'] },
  sin_sello_verde: { 1:['6','7','8','9'], 2:['0','1','2','3'], 3:['4','5','6','7'], 4:['8','9','0','1'], 5:['2','3','4','5'] },
};

const DIA_MAP = { 1:'lunes', 2:'martes', 3:'miércoles', 4:'jueves', 5:'viernes' };

// Configuración de cada categoría para generación de temporada
// Valores alineados con restriccionVehicularService.js → getRestrictionParamsForVehicle
const TEMPORADA_CATS = [
  {
    value: 'sello_verde', label: 'Autos sello verde (1994–2011)',
    rango: '1994–2011',
    tabla: 'sello_verde', horarioInicio: '07:30', horarioFin: '21:00',
    zonaClave: 'rm_provincia_santiago_san_bernardo_puente_alto',
    zonaDescripcion: 'Región Metropolitana',
  },
  {
    value: 'camioneta_sello_verde', label: 'Camionetas sello verde (1994–2011)',
    rango: '1994–2011',
    tabla: 'sello_verde', horarioInicio: '07:30', horarioFin: '21:00',
    zonaClave: 'rm_provincia_santiago_san_bernardo_puente_alto',
    zonaDescripcion: 'Región Metropolitana',
  },
  {
    value: 'moto_sello_verde', label: 'Motos sello verde (2002–2009)',
    rango: '2002–2009',
    tabla: 'sello_verde', horarioInicio: '07:30', horarioFin: '21:00',
    zonaClave: 'rm_anillo_americo_vespucio',
    zonaDescripcion: 'Interior del anillo Américo Vespucio',
  },
  {
    value: 'sin_sello_verde', label: 'Autos sin sello verde (≤ 1993)',
    rango: '≤ 1993',
    tabla: 'sin_sello_verde', horarioInicio: '07:30', horarioFin: '21:00',
    zonaClave: 'rm_anillo_americo_vespucio',
    zonaDescripcion: 'Interior del anillo Américo Vespucio',
  },
  {
    value: 'camioneta_sin_sello_verde', label: 'Camionetas sin sello verde (≤ 1993)',
    rango: '≤ 1993',
    tabla: 'sin_sello_verde', horarioInicio: '10:00', horarioFin: '18:00',
    zonaClave: 'rm_anillo_americo_vespucio',
    zonaDescripcion: 'Interior del anillo Américo Vespucio',
  },
  {
    value: 'moto_sin_sello_verde', label: 'Motos sin sello verde (≤ 2001)',
    rango: '≤ 2001',
    tabla: 'sin_sello_verde', horarioInicio: '07:30', horarioFin: '21:00',
    zonaClave: 'rm_anillo_americo_vespucio',
    zonaDescripcion: 'Interior del anillo Américo Vespucio',
  },
  {
    value: 'camion_sin_sello_verde', label: 'Camiones / carga sin sello verde',
    rango: null,
    tabla: 'sin_sello_verde', horarioInicio: '10:00', horarioFin: '18:00',
    zonaClave: 'rm_provincia_santiago_san_bernardo_puente_alto',
    zonaDescripcion: 'Región Metropolitana',
  },
  {
    value: 'bus_sin_sello_verde', label: 'Buses sin sello verde',
    rango: null,
    tabla: 'sin_sello_verde', horarioInicio: '10:00', horarioFin: '16:00',
    zonaClave: 'rm_provincia_santiago_san_bernardo_puente_alto',
    zonaDescripcion: 'Región Metropolitana',
  },
];

function buildTemporadaRecords({ anio, region, fechaDesde, fechaHasta, categorias, saltarFeriados, estado }) {
  const activas = TEMPORADA_CATS.filter(c => categorias.includes(c.value));
  const records = [];
  const start = new Date(`${fechaDesde}T12:00:00`);
  const end   = new Date(`${fechaHasta}T12:00:00`);

  for (const d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
    const dateStr = d.toISOString().slice(0, 10);
    const dow = d.getDay();
    if (dow === 0 || dow === 6) continue;
    if (saltarFeriados && FERIADOS_TEMPORADA[dateStr]) continue;

    for (const cat of activas) {
      const digitosRestringidos = DIGITOS_ROTACION[cat.tabla][dow] || [];
      records.push({
        fecha: dateStr, anio, diaSemana: DIA_MAP[dow],
        pais: 'CL', region,
        zonaClave: cat.zonaClave, zonaDescripcion: cat.zonaDescripcion,
        categoria: cat.value, descripcionCategoria: cat.label,
        tipoRestriccion: 'restriccion_vehicular_rm',
        digitosRestringidos,
        horarioInicio: cat.horarioInicio, horarioFin: cat.horarioFin,
        aplica: true, esFestivo: false, nombreFestivo: '',
        fuente: 'generacion_temporada',
        fuenteNombre: 'Generación temporada KawésCar',
        fuenteUrl: '', estado,
      });
    }
  }
  return records;
}

const ZONAS = [
  { value: 'rm_anillo_americo_vespucio',                           label: 'Anillo Américo Vespucio (RM)' },
  { value: 'rm_provincia_santiago_san_bernardo_puente_alto',        label: 'Prov. Santiago / SB / PA (RM)' },
  { value: 'rm_fuera_anillo_provincia_santiago_san_bernardo_puente_alto', label: 'Fuera anillo Prov. Santiago (RM)' },
];

const ESTADO_CFG = {
  base_no_oficial_2026: { label: 'Base no oficial', cls: 'bg-amber-100 text-amber-800 border-amber-200' },
  oficial_mtt:          { label: 'Oficial MTT',     cls: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  manual:               { label: 'Manual',           cls: 'bg-blue-100 text-blue-800 border-blue-200' },
  estimado:             { label: 'Estimado',         cls: 'bg-amber-100 text-amber-800 border-amber-200' },
};

// Rotación 2026 — debe coincidir con RESTRICCION_2026 en restriccionVehicularService.js
const WEEK_PATTERNS = {
  'sello_verde': [
    { day: 'lunes',     digits: ['8', '9'] },
    { day: 'martes',    digits: ['0', '1'] },
    { day: 'miércoles', digits: ['2', '3'] },
    { day: 'jueves',    digits: ['4', '5'] },
    { day: 'viernes',   digits: ['6', '7'] },
  ],
  'sin_sello_verde': [
    { day: 'lunes',     digits: ['6', '7', '8', '9'] },
    { day: 'martes',    digits: ['0', '1', '2', '3'] },
    { day: 'miércoles', digits: ['4', '5', '6', '7'] },
    { day: 'jueves',    digits: ['8', '9', '0', '1'] },
    { day: 'viernes',   digits: ['2', '3', '4', '5'] },
  ],
};

// ─── helpers ───────────────────────────────────────────────────────────────────

function getDayName(fecha) {
  if (!fecha) return '';
  const d = new Date(`${fecha}T12:00:00`);
  return DAY_NAMES[d.getDay()] || '';
}

function slugify(value = '') {
  return String(value).normalize('NFD').replace(/\p{M}/gu, '').toLowerCase()
    .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}

function normalizeRegion(region) {
  return String(region || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/\s+/g, '-');
}

function buildDocId(fecha, region, categoria) {
  return `${fecha}-${normalizeRegion(region)}-${slugify(categoria)}`;
}

function addDays(dateStr, n) {
  const d = new Date(`${dateStr}T12:00:00`);
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
}

function getMondayOfWeek(dateStr) {
  const d = new Date(`${dateStr}T12:00:00`);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  return d.toISOString().slice(0, 10);
}

// ─── sub-components ────────────────────────────────────────────────────────────

function EstadoBadge({ estado }) {
  const cfg = ESTADO_CFG[estado] || { label: estado || '—', cls: 'bg-slate-100 text-slate-600 border-slate-200' };
  return <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-bold ${cfg.cls}`}>{cfg.label}</span>;
}

function DigitChips({ digits = [] }) {
  if (!digits.length) return <span className="text-slate-400 text-xs">—</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {digits.map(d => (
        <span key={d} className="inline-flex h-5 w-5 items-center justify-center rounded bg-slate-700 text-[10px] font-bold text-white">{d}</span>
      ))}
    </div>
  );
}

function DigitSelector({ value = [], onChange }) {
  const digits = Array.from({ length: 10 }, (_, i) => String(i));
  const toggle = (d) => {
    const next = value.includes(d) ? value.filter(x => x !== d) : [...value, d].sort();
    onChange(next);
  };
  return (
    <div className="flex flex-wrap gap-1.5">
      {digits.map(d => (
        <button
          key={d} type="button" onClick={() => toggle(d)}
          className={`flex h-8 w-8 items-center justify-center rounded-lg border text-sm font-bold transition-colors ${
            value.includes(d)
              ? 'border-blue-500 bg-blue-600 text-white'
              : 'border-slate-200 bg-white text-slate-600 hover:border-blue-300 hover:bg-blue-50'
          }`}
        >
          {d}
        </button>
      ))}
    </div>
  );
}

function inputCls(error) {
  return `w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300 ${error ? 'border-red-400 bg-red-50' : 'border-slate-200'}`;
}

function Field({ label, error, children, span = 1 }) {
  return (
    <div className={span === 2 ? 'col-span-2' : ''}>
      <label className="mb-1 block text-xs font-semibold text-slate-600">{label}</label>
      {children}
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  );
}

// ─── empty form ────────────────────────────────────────────────────────────────

function emptyForm() {
  return {
    fecha: '', anio: new Date().getFullYear(),
    pais: 'CL', region: 'Metropolitana', comuna: '',
    zonaClave: 'rm_anillo_americo_vespucio',
    zonaDescripcion: 'Interior del anillo Américo Vespucio',
    categoria: 'sin_sello_verde',
    descripcionCategoria: '',
    tipoRestriccion: 'restriccion_vehicular_rm',
    digitosRestringidos: [],
    horarioInicio: '07:30', horarioFin: '21:00',
    aplica: true, esFestivo: false, nombreFestivo: '',
    fuente: 'manual', fuenteNombre: 'Carga manual KawésCar', fuenteUrl: '',
    estado: 'manual', diaSemana: '',
  };
}

// ─── main component ────────────────────────────────────────────────────────────

export default function VehicleRestrictionAdminPanel() {
  const [records, setRecords]       = useState([]);
  const [loading, setLoading]       = useState(false);
  const [saving, setSaving]         = useState(false);
  const [deleting, setDeleting]     = useState(false);
  const [error, setError]           = useState('');
  const [success, setSuccess]       = useState('');
  const [filters, setFilters]       = useState({
    anio: new Date().getFullYear(), region: 'Metropolitana',
    categoria: 'todos', grupoAno: 'todos', estado: 'todos',
    fechaDesde: '', fechaHasta: '', aplica: 'todos',
  });
  const [showFilters, setShowFilters] = useState(false);
  const [sortDir, setSortDir]       = useState('desc');
  const [selected, setSelected]     = useState(new Set());
  const [showModal, setShowModal]   = useState(false);
  const [editId, setEditId]         = useState(null);
  const [form, setForm]             = useState(emptyForm());
  const [formErrors, setFormErrors] = useState({});
  const [confirmDel, setConfirmDel] = useState(null);
  const [showGenWeek, setShowGenWeek] = useState(false);
  const [showReplaceRange, setShowReplaceRange] = useState(false);
  const [genForm, setGenForm]       = useState({
    fechaLunes: '', region: 'Metropolitana', categoria: 'sin_sello_verde',
    zonaClave: 'rm_anillo_americo_vespucio', zonaDescripcion: 'Interior del anillo Américo Vespucio',
    horarioInicio: '07:30', horarioFin: '21:00',
    patron: 'sello_verde', estado: 'manual',
  });
  const [genPreview, setGenPreview] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [replaceForm, setReplaceForm] = useState({
    fechaDesde: '', fechaHasta: '', region: 'Metropolitana', categoria: 'sin_sello_verde',
  });
  const [replacing, setReplacing]   = useState(false);
  const [replacingStep, setReplacingStep] = useState('form'); // form | confirm

  const [showGenTemporada, setShowGenTemporada] = useState(false);
  const [genTemporada, setGenTemporada] = useState({
    anio: new Date().getFullYear(),
    region: 'Metropolitana',
    fechaDesde: `${new Date().getFullYear()}-05-01`,
    fechaHasta: `${new Date().getFullYear()}-08-31`,
    categorias: TEMPORADA_CATS.map(c => c.value),
    saltarFeriados: true,
    estado: 'base_no_oficial_2026',
  });
  const [genTemporadaLoading, setGenTemporadaLoading] = useState(false);

  const fns = getFunctions(app, 'us-central1');
  const fnUpsert = httpsCallable(fns, 'adminUpsertRestrictionCalendar');
  const fnDelete = httpsCallable(fns, 'adminDeleteRestrictionCalendar');
  const fnImport = httpsCallable(fns, 'importRestrictionCalendar');

  // ── load ─────────────────────────────────────────────────────────────────────

  const loadRecords = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const q = query(
        collection(db, 'restricciones_vehiculares_calendario'),
        where('anio', '==', filters.anio),
        orderBy('fecha', sortDir),
      );
      const snap = await getDocs(q);
      let data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      if (filters.region && filters.region !== 'todos')
        data = data.filter(r => r.region === filters.region);
      if (filters.categoria !== 'todos')
        data = data.filter(r => r.categoria === filters.categoria);
      if (filters.grupoAno !== 'todos') {
        const cats = CATEGORIAS.filter(c => c.grupo === filters.grupoAno).map(c => c.value);
        data = data.filter(r => cats.includes(r.categoria));
      }
      if (filters.estado !== 'todos')
        data = data.filter(r => r.estado === filters.estado);
      if (filters.aplica !== 'todos')
        data = data.filter(r => String(r.aplica) === filters.aplica);
      if (filters.fechaDesde) data = data.filter(r => r.fecha >= filters.fechaDesde);
      if (filters.fechaHasta) data = data.filter(r => r.fecha <= filters.fechaHasta);
      setRecords(data);
      setSelected(new Set());
    } catch (err) {
      setError('Error al cargar: ' + (err?.message || String(err)));
    } finally {
      setLoading(false);
    }
  }, [filters, sortDir]);

  useEffect(() => { loadRecords(); }, [loadRecords]);

  useEffect(() => {
    if (!success) return;
    const t = setTimeout(() => setSuccess(''), 4000);
    return () => clearTimeout(t);
  }, [success]);

  // ── form helpers ──────────────────────────────────────────────────────────────

  function handleFechaChange(dateStr) {
    if (!dateStr) { setForm(f => ({ ...f, fecha: '' })); return; }
    const d = new Date(`${dateStr}T12:00:00`);
    setForm(f => ({
      ...f,
      fecha: dateStr,
      anio: d.getFullYear(),
      diaSemana: DAY_NAMES[d.getDay()] || '',
    }));
  }

  function validateForm() {
    const errs = {};
    if (!form.fecha) errs.fecha = 'Obligatoria';
    if (!form.categoria) errs.categoria = 'Obligatoria';
    if (!form.digitosRestringidos.length) errs.digitosRestringidos = 'Selecciona al menos un dígito';
    setFormErrors(errs);
    return Object.keys(errs).length === 0;
  }

  function openNew() {
    setEditId(null); setForm(emptyForm()); setFormErrors({}); setShowModal(true);
  }

  function openEdit(rec) {
    setEditId(rec.id);
    setForm({
      fecha: rec.fecha || '', anio: rec.anio || new Date().getFullYear(),
      pais: rec.pais || 'CL', region: rec.region || 'Metropolitana',
      comuna: rec.comuna || '',
      zonaClave: rec.zonaClave || 'rm_anillo_americo_vespucio',
      zonaDescripcion: rec.zonaDescripcion || '',
      categoria: rec.categoria || 'sin_sello_verde',
      descripcionCategoria: rec.descripcionCategoria || '',
      tipoRestriccion: rec.tipoRestriccion || 'restriccion_vehicular_rm',
      digitosRestringidos: Array.isArray(rec.digitosRestringidos) ? rec.digitosRestringidos : [],
      horarioInicio: rec.horarioInicio || '07:30', horarioFin: rec.horarioFin || '21:00',
      aplica: rec.aplica !== false, esFestivo: Boolean(rec.esFestivo),
      nombreFestivo: rec.nombreFestivo || '',
      fuente: rec.fuente || 'manual', fuenteNombre: rec.fuenteNombre || '',
      fuenteUrl: rec.fuenteUrl || '', estado: rec.estado || 'manual',
      diaSemana: rec.diaSemana || '',
    });
    setFormErrors({}); setShowModal(true);
  }

  function openDuplicate(rec) {
    setEditId(null);
    setForm({
      fecha: addDays(rec.fecha, 1), anio: rec.anio || new Date().getFullYear(),
      pais: rec.pais || 'CL', region: rec.region || 'Metropolitana',
      comuna: rec.comuna || '',
      zonaClave: rec.zonaClave || 'rm_anillo_americo_vespucio',
      zonaDescripcion: rec.zonaDescripcion || '',
      categoria: rec.categoria || 'sin_sello_verde',
      descripcionCategoria: rec.descripcionCategoria || '',
      tipoRestriccion: rec.tipoRestriccion || 'restriccion_vehicular_rm',
      digitosRestringidos: Array.isArray(rec.digitosRestringidos) ? [...rec.digitosRestringidos] : [],
      horarioInicio: rec.horarioInicio || '07:30', horarioFin: rec.horarioFin || '21:00',
      aplica: rec.aplica !== false, esFestivo: Boolean(rec.esFestivo),
      nombreFestivo: rec.nombreFestivo || '',
      fuente: 'manual', fuenteNombre: 'Carga manual KawésCar', fuenteUrl: '',
      estado: 'manual',
      diaSemana: getDayName(addDays(rec.fecha, 1)),
    });
    setFormErrors({}); setShowModal(true);
  }

  function closeModal() { setShowModal(false); setEditId(null); setFormErrors({}); }

  // ── CRUD ──────────────────────────────────────────────────────────────────────

  async function handleSave() {
    if (!validateForm()) return;
    setSaving(true); setError('');
    try {
      const docId = editId || buildDocId(form.fecha, form.region, form.categoria);
      await fnUpsert({ record: { ...form, diaSemana: form.diaSemana || getDayName(form.fecha) }, docId: editId || null });
      setSuccess(editId ? 'Registro actualizado.' : 'Registro creado.');
      closeModal();
      await loadRecords();
    } catch (err) {
      setError('Error al guardar: ' + (err?.message || String(err)));
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(ids) {
    setDeleting(true); setError('');
    try {
      const CHUNK = 499;
      for (let i = 0; i < ids.length; i += CHUNK) {
        await fnDelete({ docIds: ids.slice(i, i + CHUNK) });
      }
      setSuccess(`${ids.length} registro${ids.length > 1 ? 's' : ''} eliminado${ids.length > 1 ? 's' : ''}.`);
      setConfirmDel(null);
      await loadRecords();
    } catch (err) {
      setError('Error al eliminar: ' + (err?.message || String(err)));
    } finally {
      setDeleting(false);
    }
  }

  // ── generate week ─────────────────────────────────────────────────────────────

  function buildWeekPreview() {
    if (!genForm.fechaLunes) return null;
    const monday = getMondayOfWeek(genForm.fechaLunes);
    const pattern = WEEK_PATTERNS[genForm.patron];
    const DAY_OFFSETS = { lunes: 0, martes: 1, 'miércoles': 2, jueves: 3, viernes: 4 };
    return pattern.map(({ day, digits }) => {
      const fecha = addDays(monday, DAY_OFFSETS[day] || 0);
      return {
        fecha, diaSemana: day, region: genForm.region,
        categoria: genForm.categoria, zonaClave: genForm.zonaClave,
        zonaDescripcion: genForm.zonaDescripcion,
        horarioInicio: genForm.horarioInicio, horarioFin: genForm.horarioFin,
        digitosRestringidos: digits,
        aplica: true, esFestivo: false, nombreFestivo: '',
        estado: genForm.estado, fuente: 'manual',
        fuenteNombre: 'Generación semanal KawésCar', fuenteUrl: '',
        anio: new Date(`${fecha}T12:00:00`).getFullYear(),
        tipoRestriccion: 'restriccion_vehicular_rm',
        pais: 'CL', descripcionCategoria: '',
      };
    });
  }

  async function handleGenerateWeek() {
    const preview = buildWeekPreview();
    if (!preview) return;
    setGenerating(true); setError('');
    try {
      await fnImport({ records: preview });
      setSuccess(`Semana generada: ${preview.length} registros guardados.`);
      setShowGenWeek(false);
      setGenPreview(null);
      await loadRecords();
    } catch (err) {
      setError('Error al generar semana: ' + (err?.message || String(err)));
    } finally {
      setGenerating(false);
    }
  }

  // ── replace range ─────────────────────────────────────────────────────────────

  async function handleReplaceRange() {
    if (!replaceForm.fechaDesde || !replaceForm.fechaHasta) {
      setError('Selecciona fecha inicio y fin.'); return;
    }
    setReplacing(true); setError('');
    try {
      const toDelete = records.filter(r =>
        r.fecha >= replaceForm.fechaDesde &&
        r.fecha <= replaceForm.fechaHasta &&
        r.region === replaceForm.region &&
        r.categoria === replaceForm.categoria
      ).map(r => r.id);
      if (toDelete.length) {
        const CHUNK = 499;
        for (let i = 0; i < toDelete.length; i += CHUNK) {
          await fnDelete({ docIds: toDelete.slice(i, i + CHUNK) });
        }
      }
      setSuccess(`Rango reemplazado: ${toDelete.length} registros eliminados. Ahora carga los nuevos registros.`);
      setShowReplaceRange(false);
      setReplacingStep('form');
      await loadRecords();
    } catch (err) {
      setError('Error al reemplazar: ' + (err?.message || String(err)));
    } finally {
      setReplacing(false);
    }
  }

  // ── generar temporada completa ────────────────────────────────────────────────

  async function handleGenerateTemporada() {
    const records = buildTemporadaRecords(genTemporada);
    if (!records.length) { setError('No hay registros a generar para ese rango.'); return; }
    setGenTemporadaLoading(true); setError('');
    try {
      let created = 0, updated = 0;
      const CHUNK = 500;
      for (let i = 0; i < records.length; i += CHUNK) {
        const res = await fnImport({ records: records.slice(i, i + CHUNK) });
        created += res.data?.created || 0;
        updated += res.data?.updated || 0;
      }
      setSuccess(`Temporada generada: ${created} nuevos, ${updated} actualizados (${records.length} total).`);
      setShowGenTemporada(false);
      await loadRecords();
    } catch (err) {
      setError('Error al generar temporada: ' + (err?.message || String(err)));
    } finally {
      setGenTemporadaLoading(false);
    }
  }

  // ── selection ─────────────────────────────────────────────────────────────────

  const allSel = records.length > 0 && selected.size === records.length;
  const toggleAll = () => setSelected(allSel ? new Set() : new Set(records.map(r => r.id)));
  const toggleOne = (id) => setSelected(prev => {
    const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s;
  });

  // ── render ────────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-4">

      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold text-slate-800">Restricción vehicular · Calendario</h2>
          <p className="text-sm text-slate-500">
            {records.length} registros · <span className="font-mono text-xs">restricciones_vehiculares_calendario</span>
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {selected.size > 0 && (
            <button
              onClick={() => setConfirmDel({ ids: [...selected], label: `${selected.size} registros` })}
              className="flex items-center gap-1.5 rounded-lg border border-red-200 bg-red-50 px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-100"
            >
              <Trash2 className="h-4 w-4" /> Eliminar ({selected.size})
            </button>
          )}
          <button onClick={() => setShowReplaceRange(true)}
            className="flex items-center gap-1.5 rounded-lg border border-orange-200 bg-orange-50 px-3 py-1.5 text-sm font-medium text-orange-700 hover:bg-orange-100">
            Reemplazar rango
          </button>
          <button onClick={() => setShowGenTemporada(true)}
            className="flex items-center gap-1.5 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-sm font-medium text-emerald-700 hover:bg-emerald-100">
            <Calendar className="h-4 w-4" /> Generar temporada
          </button>
          <button onClick={() => { setShowGenWeek(true); setGenPreview(null); }}
            className="flex items-center gap-1.5 rounded-lg border border-violet-200 bg-violet-50 px-3 py-1.5 text-sm font-medium text-violet-700 hover:bg-violet-100">
            <Calendar className="h-4 w-4" /> Generar semana
          </button>
          <button onClick={loadRecords} disabled={loading}
            className="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-600 hover:bg-slate-50">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button onClick={openNew}
            className="flex items-center gap-1.5 rounded-lg bg-blue-600 px-4 py-1.5 text-sm font-semibold text-white hover:bg-blue-700">
            <Plus className="h-4 w-4" /> Nuevo registro
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
        <div className="flex flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <label className="text-xs font-semibold uppercase text-slate-500">Año</label>
            <select value={filters.anio} onChange={e => setFilters(f => ({ ...f, anio: Number(e.target.value) }))}
              className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-sm">
              {[2024, 2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs font-semibold uppercase text-slate-500">Región</label>
            <select value={filters.region} onChange={e => setFilters(f => ({ ...f, region: e.target.value }))}
              className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-sm">
              <option value="todos">Todas</option>
              <option value="Metropolitana">Metropolitana</option>
              <option value="Valparaíso">Valparaíso</option>
            </select>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs font-semibold uppercase text-slate-500">Año vehículo</label>
            <select value={filters.grupoAno} onChange={e => setFilters(f => ({ ...f, grupoAno: e.target.value, categoria: 'todos' }))}
              className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-sm">
              <option value="todos">Todos</option>
              <option value="sello_verde">Con sello verde (1994–2011)</option>
              <option value="sin_sello_verde">Sin sello verde (≤ 1993)</option>
            </select>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs font-semibold uppercase text-slate-500">Categoría</label>
            <select value={filters.categoria} onChange={e => setFilters(f => ({ ...f, categoria: e.target.value }))}
              className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-sm">
              <option value="todos">Todas</option>
              {(filters.grupoAno !== 'todos'
                ? CATEGORIAS.filter(c => c.grupo === filters.grupoAno)
                : CATEGORIAS
              ).map(c => (
                <option key={c.value} value={c.value}>
                  {c.label}{c.rango ? ` (${c.rango})` : ''}
                </option>
              ))}
            </select>
          </div>
          <button onClick={() => setShowFilters(v => !v)}
            className="flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2 py-1 text-sm text-slate-600 hover:bg-slate-100">
            {showFilters ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />} Más filtros
          </button>
          <button onClick={() => setSortDir(d => d === 'desc' ? 'asc' : 'desc')}
            className="flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2 py-1 text-sm text-slate-600 hover:bg-slate-100">
            {sortDir === 'desc' ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
            {sortDir === 'desc' ? 'Recientes' : 'Antiguos'}
          </button>
        </div>
        {showFilters && (
          <div className="mt-3 flex flex-wrap gap-3 border-t border-slate-200 pt-3">
            <div className="flex items-center gap-2">
              <label className="text-xs font-semibold uppercase text-slate-500">Estado</label>
              <select value={filters.estado} onChange={e => setFilters(f => ({ ...f, estado: e.target.value }))}
                className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-sm">
                <option value="todos">Todos</option>
                {Object.entries(ESTADO_CFG).map(([v, c]) => <option key={v} value={v}>{c.label}</option>)}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs font-semibold uppercase text-slate-500">Aplica</label>
              <select value={filters.aplica} onChange={e => setFilters(f => ({ ...f, aplica: e.target.value }))}
                className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-sm">
                <option value="todos">Todos</option>
                <option value="true">Sí</option>
                <option value="false">No</option>
              </select>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs font-semibold uppercase text-slate-500">Desde</label>
              <input type="date" value={filters.fechaDesde}
                onChange={e => setFilters(f => ({ ...f, fechaDesde: e.target.value }))}
                className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-sm" />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs font-semibold uppercase text-slate-500">Hasta</label>
              <input type="date" value={filters.fechaHasta}
                onChange={e => setFilters(f => ({ ...f, fechaHasta: e.target.value }))}
                className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-sm" />
            </div>
          </div>
        )}
      </div>

      {/* Alerts */}
      {error && (
        <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          <AlertTriangle className="h-4 w-4 shrink-0" />{error}
          <button onClick={() => setError('')} className="ml-auto"><X className="h-4 w-4" /></button>
        </div>
      )}
      {success && (
        <div className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-700">
          <CheckCircle2 className="h-4 w-4 shrink-0" />{success}
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
        {loading ? (
          <div className="flex items-center justify-center gap-2 py-12 text-slate-400">
            <Loader2 className="h-5 w-5 animate-spin" /> Cargando calendario...
          </div>
        ) : records.length === 0 ? (
          <div className="py-12 text-center text-slate-400">
            <p className="font-medium">No hay calendario de restricción cargado para este filtro.</p>
            <p className="mt-1 text-sm">Cambia los filtros, agrega un registro o genera una semana.</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                <th className="w-8 px-3 py-3">
                  <button onClick={toggleAll}>
                    {allSel ? <CheckSquare className="h-4 w-4 text-blue-600" /> : <Square className="h-4 w-4" />}
                  </button>
                </th>
                <th className="px-3 py-3">Fecha</th>
                <th className="px-3 py-3">Día</th>
                <th className="px-3 py-3">Región</th>
                <th className="px-3 py-3">Categoría</th>
                <th className="px-3 py-3">Dígitos</th>
                <th className="px-3 py-3">Horario</th>
                <th className="px-3 py-3">Aplica</th>
                <th className="px-3 py-3">Estado</th>
                <th className="px-3 py-3">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {records.map(rec => (
                <tr key={rec.id} className={`transition-colors hover:bg-slate-50 ${selected.has(rec.id) ? 'bg-blue-50' : ''}`}>
                  <td className="px-3 py-2">
                    <button onClick={() => toggleOne(rec.id)}>
                      {selected.has(rec.id)
                        ? <CheckSquare className="h-4 w-4 text-blue-600" />
                        : <Square className="h-4 w-4 text-slate-300" />}
                    </button>
                  </td>
                  <td className="px-3 py-2 font-mono text-slate-800">{rec.fecha}</td>
                  <td className="px-3 py-2 capitalize text-slate-500">{rec.diaSemana || '—'}</td>
                  <td className="px-3 py-2 text-slate-600 text-xs">{rec.region || '—'}</td>
                  <td className="px-3 py-2 text-xs text-slate-600">
                    {(() => {
                      const meta = CATEGORIAS.find(c => c.value === rec.categoria);
                      if (!meta) return <span className="font-mono text-[10px] text-slate-400">{rec.categoria || '—'}</span>;
                      return (
                        <span>
                          {meta.label}
                          {meta.rango && <span className="ml-1 text-[10px] text-slate-400 font-mono">{meta.rango}</span>}
                        </span>
                      );
                    })()}
                  </td>
                  <td className="px-3 py-2"><DigitChips digits={rec.digitosRestringidos} /></td>
                  <td className="px-3 py-2 text-xs text-slate-600 whitespace-nowrap">
                    {rec.horarioInicio}–{rec.horarioFin}
                  </td>
                  <td className="px-3 py-2">
                    {rec.aplica === false
                      ? <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-slate-600">No aplica</span>
                      : rec.esFestivo
                        ? <span className="inline-flex items-center rounded-full border border-blue-200 bg-blue-100 px-2 py-0.5 text-[10px] font-bold text-blue-700">Festivo</span>
                        : <span className="inline-flex items-center rounded-full border border-emerald-200 bg-emerald-100 px-2 py-0.5 text-[10px] font-bold text-emerald-700">Sí</span>}
                  </td>
                  <td className="px-3 py-2"><EstadoBadge estado={rec.estado} /></td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-1.5">
                      <button onClick={() => openEdit(rec)} title="Editar"
                        className="rounded p-1 text-slate-500 hover:bg-slate-100"><Pencil className="h-3.5 w-3.5" /></button>
                      <button onClick={() => openDuplicate(rec)} title="Duplicar día"
                        className="rounded p-1 text-slate-500 hover:bg-slate-100"><Copy className="h-3.5 w-3.5" /></button>
                      <button
                        onClick={() => setConfirmDel({ ids: [rec.id], label: `la restricción del ${rec.fecha}` })}
                        title="Eliminar" className="rounded p-1 text-slate-400 hover:bg-red-50 hover:text-red-600">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Form Modal ──────────────────────────────────────────────────────────── */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white shadow-2xl">
            <div className="sticky top-0 flex items-center justify-between border-b border-slate-100 bg-white px-6 py-4">
              <h3 className="text-base font-bold text-slate-900">
                {editId ? 'Editar restricción' : 'Nuevo registro restricción'}
              </h3>
              <button onClick={closeModal} className="text-slate-400 hover:text-slate-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="space-y-4 p-6">
              <div className="grid grid-cols-2 gap-4">
                <Field label="Fecha *" error={formErrors.fecha} span={1}>
                  <input type="date" value={form.fecha} onChange={e => handleFechaChange(e.target.value)}
                    className={inputCls(formErrors.fecha)} />
                </Field>
                <Field label="Día semana" span={1}>
                  <input type="text" value={form.diaSemana} readOnly
                    className="w-full rounded-lg border border-slate-100 bg-slate-50 px-3 py-2 text-sm capitalize text-slate-500" />
                </Field>
                <Field label="País" span={1}>
                  <input type="text" value={form.pais} onChange={e => setForm(f => ({ ...f, pais: e.target.value }))}
                    className={inputCls()} />
                </Field>
                <Field label="Región" span={1}>
                  <input type="text" value={form.region} onChange={e => setForm(f => ({ ...f, region: e.target.value }))}
                    className={inputCls()} />
                </Field>
                <Field label="Zona clave" span={2}>
                  <select value={form.zonaClave} onChange={e => setForm(f => ({ ...f, zonaClave: e.target.value }))}
                    className={inputCls()}>
                    {ZONAS.map(z => <option key={z.value} value={z.value}>{z.label}</option>)}
                  </select>
                </Field>
                <Field label="Categoría *" error={formErrors.categoria} span={2}>
                  <select value={form.categoria} onChange={e => setForm(f => ({ ...f, categoria: e.target.value }))}
                    className={inputCls(formErrors.categoria)}>
                    {CATEGORIAS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                </Field>
                <Field label="Dígitos restringidos *" error={formErrors.digitosRestringidos} span={2}>
                  <DigitSelector value={form.digitosRestringidos}
                    onChange={v => setForm(f => ({ ...f, digitosRestringidos: v }))} />
                  {formErrors.digitosRestringidos && (
                    <p className="mt-1 text-xs text-red-600">{formErrors.digitosRestringidos}</p>
                  )}
                </Field>
                <Field label="Horario inicio" span={1}>
                  <input type="time" value={form.horarioInicio}
                    onChange={e => setForm(f => ({ ...f, horarioInicio: e.target.value }))}
                    className={inputCls()} />
                </Field>
                <Field label="Horario fin" span={1}>
                  <input type="time" value={form.horarioFin}
                    onChange={e => setForm(f => ({ ...f, horarioFin: e.target.value }))}
                    className={inputCls()} />
                </Field>
                <div className="col-span-2 flex flex-wrap gap-6">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={form.aplica}
                      onChange={e => setForm(f => ({ ...f, aplica: e.target.checked }))}
                      className="rounded" />
                    <span className="text-sm font-medium text-slate-700">Aplica</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={form.esFestivo}
                      onChange={e => setForm(f => ({ ...f, esFestivo: e.target.checked }))}
                      className="rounded" />
                    <span className="text-sm font-medium text-slate-700">Es festivo</span>
                  </label>
                </div>
                {form.esFestivo && (
                  <Field label="Nombre festivo" span={2}>
                    <input type="text" value={form.nombreFestivo}
                      onChange={e => setForm(f => ({ ...f, nombreFestivo: e.target.value }))}
                      className={inputCls()} />
                  </Field>
                )}
                <Field label="Estado" span={1}>
                  <select value={form.estado} onChange={e => setForm(f => ({ ...f, estado: e.target.value }))}
                    className={inputCls()}>
                    <option value="base_no_oficial_2026">Base no oficial 2026</option>
                    <option value="oficial_mtt">Oficial MTT</option>
                    <option value="manual">Manual</option>
                    <option value="estimado">Estimado</option>
                  </select>
                </Field>
                <Field label="Fuente" span={1}>
                  <input type="text" value={form.fuente}
                    onChange={e => setForm(f => ({ ...f, fuente: e.target.value }))}
                    className={inputCls()} />
                </Field>
                <Field label="Nombre fuente" span={1}>
                  <input type="text" value={form.fuenteNombre}
                    onChange={e => setForm(f => ({ ...f, fuenteNombre: e.target.value }))}
                    className={inputCls()} />
                </Field>
                <Field label="URL fuente" span={1}>
                  <input type="url" value={form.fuenteUrl} placeholder="https://..."
                    onChange={e => setForm(f => ({ ...f, fuenteUrl: e.target.value }))}
                    className={inputCls()} />
                </Field>
              </div>

              {error && (
                <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                  <AlertTriangle className="h-4 w-4 shrink-0" />{error}
                </div>
              )}

              <div className="flex justify-end gap-3 pt-2">
                <button onClick={closeModal}
                  className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50">
                  Cancelar
                </button>
                <button onClick={handleSave} disabled={saving}
                  className="flex items-center gap-2 rounded-lg bg-blue-600 px-5 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50">
                  {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                  {saving ? 'Guardando...' : 'Guardar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Generate Week Modal ─────────────────────────────────────────────────── */}
      {showGenWeek && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="max-h-[90vh] w-full max-w-xl overflow-y-auto rounded-2xl bg-white shadow-2xl">
            <div className="sticky top-0 flex items-center justify-between border-b border-slate-100 bg-white px-6 py-4">
              <h3 className="text-base font-bold text-slate-900">Generar semana desde patrón</h3>
              <button onClick={() => { setShowGenWeek(false); setGenPreview(null); }}
                className="text-slate-400 hover:text-slate-600"><X className="h-5 w-5" /></button>
            </div>
            <div className="space-y-4 p-6">
              <div className="grid grid-cols-2 gap-4">
                <Field label="Fecha de inicio (cualquier día de la semana)" span={2}>
                  <input type="date" value={genForm.fechaLunes}
                    onChange={e => setGenForm(f => ({ ...f, fechaLunes: e.target.value }))}
                    className={inputCls()} />
                  <p className="mt-1 text-xs text-slate-400">Se usará el lunes de esa semana como punto de partida.</p>
                </Field>
                <Field label="Región" span={1}>
                  <input type="text" value={genForm.region}
                    onChange={e => setGenForm(f => ({ ...f, region: e.target.value }))}
                    className={inputCls()} />
                </Field>
                <Field label="Categoría" span={1}>
                  <select value={genForm.categoria}
                    onChange={e => setGenForm(f => ({ ...f, categoria: e.target.value }))}
                    className={inputCls()}>
                    {CATEGORIAS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                </Field>
                <Field label="Zona clave" span={2}>
                  <select value={genForm.zonaClave}
                    onChange={e => setGenForm(f => ({ ...f, zonaClave: e.target.value }))}
                    className={inputCls()}>
                    {ZONAS.map(z => <option key={z.value} value={z.value}>{z.label}</option>)}
                  </select>
                </Field>
                <Field label="Horario inicio" span={1}>
                  <input type="time" value={genForm.horarioInicio}
                    onChange={e => setGenForm(f => ({ ...f, horarioInicio: e.target.value }))}
                    className={inputCls()} />
                </Field>
                <Field label="Horario fin" span={1}>
                  <input type="time" value={genForm.horarioFin}
                    onChange={e => setGenForm(f => ({ ...f, horarioFin: e.target.value }))}
                    className={inputCls()} />
                </Field>
                <Field label="Patrón de dígitos" span={2}>
                  <div className="flex flex-col gap-2">
                    {[
                      { value: 'sello_verde',    label: 'Con sello verde — 2 dígitos/día (L:8,9 · M:0,1 · X:2,3 · J:4,5 · V:6,7)' },
                      { value: 'sin_sello_verde', label: 'Sin sello verde — 4 dígitos/día (L:6-9 · M:0-3 · X:4-7 · J:8,9,0,1 · V:2-5)' },
                    ].map(p => (
                      <label key={p.value} className="flex items-start gap-2 cursor-pointer flex-1">
                        <input type="radio" name="patron" value={p.value} checked={genForm.patron === p.value}
                          onChange={() => setGenForm(f => ({ ...f, patron: p.value }))}
                          className="mt-0.5" />
                        <span className="text-sm text-slate-700">{p.label}</span>
                      </label>
                    ))}
                  </div>
                </Field>
                <Field label="Estado" span={1}>
                  <select value={genForm.estado} onChange={e => setGenForm(f => ({ ...f, estado: e.target.value }))}
                    className={inputCls()}>
                    <option value="base_no_oficial_2026">Base no oficial</option>
                    <option value="manual">Manual</option>
                    <option value="estimado">Estimado</option>
                  </select>
                </Field>
              </div>

              <button
                onClick={() => setGenPreview(buildWeekPreview())}
                disabled={!genForm.fechaLunes}
                className="w-full rounded-lg border border-violet-200 bg-violet-50 py-2 text-sm font-semibold text-violet-700 hover:bg-violet-100 disabled:opacity-40"
              >
                Vista previa
              </button>

              {genPreview && (
                <div className="rounded-xl border border-slate-200 overflow-hidden">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-100 text-left text-[10px] font-semibold uppercase text-slate-500">
                        <th className="px-3 py-2">Fecha</th>
                        <th className="px-3 py-2">Día</th>
                        <th className="px-3 py-2">Dígitos</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {genPreview.map(r => (
                        <tr key={r.fecha}>
                          <td className="px-3 py-2 font-mono">{r.fecha}</td>
                          <td className="px-3 py-2 capitalize text-slate-600">{r.diaSemana}</td>
                          <td className="px-3 py-2"><DigitChips digits={r.digitosRestringidos} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {genPreview && (
                <div className="flex justify-end gap-3">
                  <button onClick={() => { setShowGenWeek(false); setGenPreview(null); }}
                    className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-600 hover:bg-slate-50">
                    Cancelar
                  </button>
                  <button onClick={handleGenerateWeek} disabled={generating}
                    className="flex items-center gap-2 rounded-lg bg-violet-600 px-5 py-2 text-sm font-semibold text-white hover:bg-violet-700 disabled:opacity-50">
                    {generating && <Loader2 className="h-4 w-4 animate-spin" />}
                    {generating ? 'Guardando...' : `Guardar ${genPreview.length} registros`}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Replace Range Modal ─────────────────────────────────────────────────── */}
      {showReplaceRange && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
              <h3 className="text-base font-bold text-slate-900">Reemplazar rango</h3>
              <button onClick={() => { setShowReplaceRange(false); setReplacingStep('form'); }}
                className="text-slate-400 hover:text-slate-600"><X className="h-5 w-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              {replacingStep === 'form' ? (
                <>
                  <div className="rounded-lg border border-orange-200 bg-orange-50 p-3 text-sm text-orange-800">
                    <strong>Atención:</strong> Esta acción eliminará registros existentes del rango seleccionado para esta categoría y región.
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <Field label="Fecha inicio" span={1}>
                      <input type="date" value={replaceForm.fechaDesde}
                        onChange={e => setReplaceForm(f => ({ ...f, fechaDesde: e.target.value }))}
                        className={inputCls()} />
                    </Field>
                    <Field label="Fecha fin" span={1}>
                      <input type="date" value={replaceForm.fechaHasta}
                        onChange={e => setReplaceForm(f => ({ ...f, fechaHasta: e.target.value }))}
                        className={inputCls()} />
                    </Field>
                    <Field label="Región" span={1}>
                      <input type="text" value={replaceForm.region}
                        onChange={e => setReplaceForm(f => ({ ...f, region: e.target.value }))}
                        className={inputCls()} />
                    </Field>
                    <Field label="Categoría" span={1}>
                      <select value={replaceForm.categoria}
                        onChange={e => setReplaceForm(f => ({ ...f, categoria: e.target.value }))}
                        className={inputCls()}>
                        {CATEGORIAS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                      </select>
                    </Field>
                  </div>
                  <div className="flex justify-end gap-3 pt-2">
                    <button onClick={() => { setShowReplaceRange(false); setReplacingStep('form'); }}
                      className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-600 hover:bg-slate-50">
                      Cancelar
                    </button>
                    <button
                      onClick={() => setReplacingStep('confirm')}
                      disabled={!replaceForm.fechaDesde || !replaceForm.fechaHasta}
                      className="rounded-lg bg-orange-600 px-4 py-2 text-sm font-semibold text-white hover:bg-orange-700 disabled:opacity-40">
                      Continuar
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
                    <p className="font-bold mb-1">¿Confirmas la eliminación?</p>
                    <p>Se eliminarán los registros de <strong>{replaceForm.fechaDesde}</strong> a <strong>{replaceForm.fechaHasta}</strong> para la región <strong>{replaceForm.region}</strong> y categoría <strong>{replaceForm.categoria}</strong>.</p>
                    <p className="mt-2 text-xs">Esta acción no se puede deshacer. Después podrás cargar nuevos registros para ese rango.</p>
                  </div>
                  <div className="flex justify-end gap-3">
                    <button onClick={() => setReplacingStep('form')}
                      className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-600 hover:bg-slate-50">
                      Volver
                    </button>
                    <button onClick={handleReplaceRange} disabled={replacing}
                      className="flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50">
                      {replacing && <Loader2 className="h-4 w-4 animate-spin" />}
                      Eliminar rango
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Confirm Delete ──────────────────────────────────────────────────────── */}
      {/* ── Modal: Generar temporada completa ──────────────────────────────────── */}
      {showGenTemporada && (() => {
        const preview = buildTemporadaRecords(genTemporada);
        const toggleCat = (v) => setGenTemporada(f => ({
          ...f,
          categorias: f.categorias.includes(v)
            ? f.categorias.filter(c => c !== v)
            : [...f.categorias, v],
        }));
        const allCats = genTemporada.categorias.length === TEMPORADA_CATS.length;
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white shadow-2xl">
              <div className="sticky top-0 flex items-center justify-between border-b border-slate-100 bg-white px-6 py-4">
                <div>
                  <h3 className="text-base font-bold text-slate-900">Generar temporada completa</h3>
                  <p className="text-xs text-slate-500 mt-0.5">Crea registros para todas las categorías en el período indicado</p>
                </div>
                <button onClick={() => setShowGenTemporada(false)} className="text-slate-400 hover:text-slate-600">
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="p-6 space-y-5">
                {/* Período */}
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Año" span={1}>
                    <select value={genTemporada.anio}
                      onChange={e => setGenTemporada(f => ({ ...f, anio: Number(e.target.value), fechaDesde: `${e.target.value}-05-01`, fechaHasta: `${e.target.value}-08-31` }))}
                      className={inputCls()}>
                      {[2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
                    </select>
                  </Field>
                  <Field label="Región" span={1}>
                    <input type="text" value={genTemporada.region}
                      onChange={e => setGenTemporada(f => ({ ...f, region: e.target.value }))}
                      className={inputCls()} />
                  </Field>
                  <Field label="Fecha inicio" span={1}>
                    <input type="date" value={genTemporada.fechaDesde}
                      onChange={e => setGenTemporada(f => ({ ...f, fechaDesde: e.target.value }))}
                      className={inputCls()} />
                  </Field>
                  <Field label="Fecha fin" span={1}>
                    <input type="date" value={genTemporada.fechaHasta}
                      onChange={e => setGenTemporada(f => ({ ...f, fechaHasta: e.target.value }))}
                      className={inputCls()} />
                  </Field>
                </div>

                {/* Categorías */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-xs font-semibold text-slate-600 uppercase">Categorías a generar</label>
                    <button type="button"
                      onClick={() => setGenTemporada(f => ({ ...f, categorias: allCats ? [] : TEMPORADA_CATS.map(c => c.value) }))}
                      className="text-xs text-blue-600 hover:underline">
                      {allCats ? 'Deseleccionar todas' : 'Seleccionar todas'}
                    </button>
                  </div>
                  <div className="divide-y divide-slate-100 rounded-xl border border-slate-200 overflow-hidden">
                    {TEMPORADA_CATS.map(cat => (
                      <label key={cat.value} className="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-slate-50">
                        <input type="checkbox"
                          checked={genTemporada.categorias.includes(cat.value)}
                          onChange={() => toggleCat(cat.value)}
                          className="rounded accent-blue-600" />
                        <span className="flex-1 text-sm text-slate-700">{cat.label}</span>
                        <span className="text-xs text-slate-400 font-mono whitespace-nowrap">
                          {cat.horarioInicio}–{cat.horarioFin}
                          {cat.tabla === 'sello_verde' ? ' · 2 díg' : ' · 4 díg'}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Opciones */}
                <div className="flex flex-wrap gap-6 items-center">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={genTemporada.saltarFeriados}
                      onChange={e => setGenTemporada(f => ({ ...f, saltarFeriados: e.target.checked }))}
                      className="rounded accent-blue-600" />
                    <span className="text-sm text-slate-700">Saltar feriados</span>
                  </label>
                  <div className="flex items-center gap-2">
                    <label className="text-xs font-semibold text-slate-500 uppercase">Estado</label>
                    <select value={genTemporada.estado}
                      onChange={e => setGenTemporada(f => ({ ...f, estado: e.target.value }))}
                      className="rounded-lg border border-slate-200 px-2 py-1 text-sm">
                      <option value="base_no_oficial_2026">Base no oficial 2026</option>
                      <option value="manual">Manual</option>
                      <option value="oficial_mtt">Oficial MTT</option>
                    </select>
                  </div>
                </div>

                {/* Resumen */}
                <div className={`rounded-xl border px-4 py-3 ${preview.length > 0 ? 'border-emerald-200 bg-emerald-50' : 'border-slate-200 bg-slate-50'}`}>
                  <p className={`text-sm font-bold ${preview.length > 0 ? 'text-emerald-800' : 'text-slate-500'}`}>
                    {preview.length > 0
                      ? `${preview.length} registros a generar (${genTemporada.categorias.length} categorías × días hábiles)`
                      : 'Selecciona al menos una categoría y un rango de fechas válido.'}
                  </p>
                  {preview.length > 0 && (
                    <p className="text-xs text-emerald-700 mt-0.5">
                      Los registros existentes serán actualizados (no duplicados). Los nuevos se crearán.
                    </p>
                  )}
                </div>

                {error && (
                  <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                    <AlertTriangle className="h-4 w-4 shrink-0" />{error}
                  </div>
                )}

                <div className="flex justify-end gap-3 pt-1">
                  <button onClick={() => setShowGenTemporada(false)}
                    className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-600 hover:bg-slate-50">
                    Cancelar
                  </button>
                  <button onClick={handleGenerateTemporada}
                    disabled={genTemporadaLoading || preview.length === 0}
                    className="flex items-center gap-2 rounded-lg bg-emerald-600 px-5 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50">
                    {genTemporadaLoading && <Loader2 className="h-4 w-4 animate-spin" />}
                    {genTemporadaLoading ? 'Generando...' : `Generar ${preview.length} registros`}
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {confirmDel && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-2xl">
            <h3 className="font-bold text-slate-900">Confirmar eliminación</h3>
            <p className="mt-2 text-sm text-slate-600">
              {confirmDel.ids.length === 1
                ? `¿Eliminar ${confirmDel.label}? Esta acción no se puede deshacer.`
                : `Eliminarás ${confirmDel.ids.length} registros del calendario de restricción. Esta acción no se puede deshacer.`}
            </p>
            <div className="mt-5 flex justify-end gap-3">
              <button onClick={() => setConfirmDel(null)}
                className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-600 hover:bg-slate-50">
                Cancelar
              </button>
              <button onClick={() => handleDelete(confirmDel.ids)} disabled={deleting}
                className="flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50">
                {deleting && <Loader2 className="h-4 w-4 animate-spin" />}
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
